import { useEffect, useState } from 'react';

import { css } from '@emotion/react';
import dynamic from 'next/dynamic';
import Image from 'next/legacy/image';
import { useRouter } from 'next/router';

import { useAppStore } from 'src/hooks/useAppStore';
import { fetchLapserAdvanceRenewalDetails } from 'src/services/apiRequests/LapserAndAdvanceRenewalDetails';
import { getActiveSubscription } from 'src/services/apiRequests/SubscriptionService';
import { fetchUserData } from 'src/services/apiRequests/UserService';
import type { LapserDetails } from 'src/types/lapser';
import { mixpanelObject } from 'src/utils/analytics';
import { userDataPropertySetter } from 'src/utils/analytics/mixpanel/mixpanelHelper';
import { setUserTypeInLS } from 'src/utils/auth-utils/UserTypeHandler';
import { clearSubscriptionNudgePopupTimer } from 'src/utils/clearSubscriptionNudgePopupTimer';
import {
  IS_RENEWAL_PLAN_SAME_AS_EXPIRED_FOR_LAPSER,
  ISSUBSCRIPTIONEXPIRED,
  PARTNER_NAME,
  QWIKCILVER_TYPE,
  SETTINGS,
  USER_CONSENTS,
  USER_TOKEN,
  USER_TYPE,
  UTM_SOURCE,
  VISITED_PAYMENT_PAGE
} from 'src/utils/constants/localStorageConstants';
import {
  HamburgerMenuLinks,
  HeaderLinks,
  NavMenuLinks
} from 'src/utils/constants/RouterConstants';
import { fetchEntitlementUserType } from 'src/utils/fetchEntitlementUserType';
import { getDeviceWidth } from 'src/utils/getDeviceWidth';
import { getLapserConfig } from 'src/utils/lapserUtils';
import { isFeatureEnabled } from 'src/utils/RemoteConfig';
import { getGuestUserToken, navigationHandler } from 'src/utils/UtilityMethods';

import {
  burgerCTAMixpanelEvent,
  calculateScrollHeight,
  getLogoWidth,
  getMenuCount,
  getUserContactInfo,
  getUserName
} from './HeaderHandler';
import * as style from './HeaderStyle';
import {
  MIXPANEL_BUY_PLAN,
  MIXPANEL_CROSS,
  MIXPANEL_CTA,
  MIXPANEL_HAMBURGER,
  MIXPANEL_HEADER,
  MIXPANEL_LOGIN,
  MIXPANEL_REGISTER
} from '../../utils/analytics/mixpanel/mixpanelConstants';
import {
  deleteSessionStorage,
  getLocalStorage,
  getSessionStorage,
  setSessionStorage
} from '../../utils/browserStorageHandler';
import {
  CountryCode,
  DisplayLanguage,
  HeaderComponentImageAlt,
  HeaderImageUrl,
  PartnerPlatforms,
  UserTypes
} from '../../utils/constants/stringConstants';
import {
  DEVICE_WIDTH_CONSTANTS,
  FeatureFlagConstants
} from '../../utils/constants/utilConstants';
import AnimatedButton from '../animatedButton';
import { BackButton, shouldShowBackButton } from '../backButton';
import HamburgerMenuWrapper from '../hamburgerMenu/HamburgerMenuWrapper';
import LanguagePopup from '../languagePopup/LanguagePopup';
import NavMenu from '../navMenu/NavMenu';
import Portal from '../portal/Portal';
import ProfileMenu from '../profileMenu/ProfileMenu';

const SearchBox = dynamic(() => import('../searchBox/SearchBox'), {
  loading: () => null,
  ssr: false
});

const Header = () => {
  const {
    isUserLoggedIn,
    usersData,
    translations,
    usersLocationData,
    usersPreferenceData,
    tvodRentals,
    appConfig,
    initialLaunchData,
    subscriptionData = {
      currentActiveSubscriptions: [],
      maxPricePack: {}
    },
    parentalData,
    remoteConfig,
    setAppConfig,
    setPopups,
    bestOfferingPlan,
    setSubscriptionData,
    setUserPackType,
    setTvodRentals,
    setUsersData,
    setUpgradeData
  } = useAppStore();

  const referralSubscriptionNudgeEnabled = isFeatureEnabled(
    remoteConfig,
    FeatureFlagConstants.CONFIG_REFERRAL
  );

  const countryCode = usersLocationData?.country_code || CountryCode.IN;
  const isGlobal = countryCode !== CountryCode.IN;
  const isGetStarted =
    initialLaunchData?.country_config?.svod_journey?.get_started || false;
  const isBotRequest = appConfig.isRequestedByBot;

  const isQwikcilverSubscribed =
    getLocalStorage(QWIKCILVER_TYPE) === UserTypes.PREMIUM;

  const headerStrings = translations?.common;
  const {
    GET_STARTED,
    UPGRADE_POPUP,
    renew_plan_header_cta,
    SUBSCRIBE,
    BUY_PLAN,
    REGISTER
  } = headerStrings || {};
  const innerWidth = appConfig?.deviceWidth as number;
  const router = useRouter();
  const { asPath } = router;
  const isSubscriptionPage =
    asPath.includes(HamburgerMenuLinks.BUY_PLAN) || asPath.includes('/apr');
  const isBackButtonRequired = shouldShowBackButton(asPath);

  const [isUpgrade, setIsUpgrade] = useState(false);
  const isMobile = innerWidth < DEVICE_WIDTH_CONSTANTS.deviceWidth1200;
  const settings = usersPreferenceData || getLocalStorage(SETTINGS);
  const displayLanguage = settings?.display_language || DisplayLanguage.ENGLISH;
  const logoWidth = getLogoWidth(appConfig?.deviceType || '', innerWidth);
  const shouldShowGlobalRegister =
    !isMobile &&
    isGlobal &&
    ((!isUserLoggedIn && !isGetStarted) || isBotRequest);

  const isRenewalPlanSameAsExpiredForLapser = getLocalStorage(
    IS_RENEWAL_PLAN_SAME_AS_EXPIRED_FOR_LAPSER
  );

  const isSubscribedUser =
    subscriptionData.currentActiveSubscriptions.length > 0;
  const isExpiredPlans = getLocalStorage(ISSUBSCRIPTIONEXPIRED) === 'true';

  const isNonSubscribedUser =
    subscriptionData.currentActiveSubscriptions.length === 0;

  const getUpgradePlans = async () => {
    const { getUpgradePlansData } = await import(
      'src/services/apiRequests/SubscriptionService'
    );

    const upgradePlans = await getUpgradePlansData(
      subscriptionData?.maxPricePack?.subscription_plan?.id
    );
    if (upgradePlans) {
      const { plans, default_plan } = upgradePlans;
      /** Upgrade button will show based on any one of the plan details
       * having purchase_allowed as true  */
      const upgradePlanData = plans.find((plan) => {
        return plan.purchase_allowed === true;
      });
      const defaultUpgradeablePlan = plans.find(
        (plan) => plan.id === default_plan && plan.purchase_allowed
      );
      if (upgradePlanData) {
        setIsUpgrade(true);
        setUpgradeData(defaultUpgradeablePlan ?? upgradePlanData);
      }
    }
  };

  const scrollHeight = calculateScrollHeight(innerWidth);

  const [hideMenu, setHideMenu] = useState(false);
  const [headerScrollPosition, setHeaderScrollPosition] = useState(false);
  const [isUserDataAvailable, setIsUserDataAvailable] = useState(false);

  const [showHamburgerMenu, setShowHamburgerMenu] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [menuCount, setMenuCount] = useState(3);
  const [isTelcoUser, setIsTelcoUser] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [lapserDetails, setLapserDetails] = useState<LapserDetails | null>(
    null
  );

  const { enabled: lapserEnabled } = getLapserConfig(remoteConfig);

  const isLapser =
    lapserEnabled &&
    lapserDetails?.lapserAdvanceRenewalNudge?.lapserAdvanceRenewal
      ?.nudgeType === 'LAPSER';

  let subscribeBtnTxt = '';
  if (isUpgrade) {
    subscribeBtnTxt = UPGRADE_POPUP;
  } else if (isRenewalPlanSameAsExpiredForLapser) {
    subscribeBtnTxt = renew_plan_header_cta;
  } else if (isGlobal) {
    subscribeBtnTxt = bestOfferingPlan
      ? `${SUBSCRIBE} @ ${bestOfferingPlan.actual_price} ${bestOfferingPlan.currency}`
      : SUBSCRIBE;
  } else {
    subscribeBtnTxt = BUY_PLAN;
  }

  // hide upgrade/subscribe btn for tatasky !isPremium
  const showSubsBtn = isTelcoUser
    ? !isPremium
    : isUpgrade || isNonSubscribedUser;

  useEffect(() => {
    fetchUser();
    getTvodRentals();
    setActiveSubscriptionDetails();
    const partnerName = getSessionStorage(PARTNER_NAME);
    if (
      partnerName === PartnerPlatforms.VIMTVAPP ||
      partnerName === PartnerPlatforms.TATASKY_PARTNER
    ) {
      setIsTelcoUser(true);
    }
    if (
      appConfig.userType === UserTypes.PREMIUM ||
      getSessionStorage(UTM_SOURCE)
    ) {
      setIsPremium(true);
    }
  }, []);

  const fetchUser = async () => {
    await getGuestUserToken();
    if (getLocalStorage(USER_TOKEN)) {
      const userData = await fetchUserData();
      if (userData?.consents) {
        setSessionStorage(USER_CONSENTS, userData.consents);
      }
      setUsersData({ ...userData });
      userDataPropertySetter(mixpanelObject, userData);
    }
  };

  const getLapserInfo = async () => {
    const lapserDetails = await fetchLapserAdvanceRenewalDetails();
    if (lapserDetails) {
      setLapserDetails(lapserDetails);
    }
  };

  useEffect(() => {
    if (
      isUserLoggedIn &&
      subscriptionData?.maxPricePack?.subscription_plan?.id
    ) {
      getUpgradePlans();
    }
  }, [subscriptionData]);

  useEffect(() => {
    if (lapserEnabled && isUserLoggedIn) {
      getLapserInfo();
    }
  }, []);

  useEffect(() => {
    const headerContainerDOMElement = document.querySelector(
      '[data-testid=headerContainer]'
    );
    const headerContainerElemObservable = new ResizeObserver(() => {
      setAppConfig({ deviceWidth: getDeviceWidth() as number });
    });
    if (headerContainerDOMElement) {
      headerContainerElemObservable.observe(headerContainerDOMElement);
    }
    return () => {
      if (headerContainerDOMElement) {
        headerContainerElemObservable.unobserve(headerContainerDOMElement);
      }
    };
  }, []);

  useEffect(() => {
    setMenuCount(getMenuCount(displayLanguage, countryCode));
    setHideMenu(false);
  }, [innerWidth, displayLanguage, countryCode]);

  useEffect(() => {
    window.addEventListener('scroll', scrollHandler);
    return () => {
      window.removeEventListener('scroll', scrollHandler);
    };
  }, [headerScrollPosition]);

  useEffect(() => {
    if (usersData) {
      setIsUserDataAvailable(true);
    }
  }, [JSON.stringify(usersData)]);

  useEffect(() => {
    if (parentalData?.showParentalPinPageFlag) {
      setShowHamburgerMenu(false);
    }
  }, [parentalData?.showParentalPinPageFlag]);

  const setActiveSubscriptionDetails = async () => {
    const token = getLocalStorage(USER_TOKEN);
    if (token) {
      const subscriptionDataResponse = await getActiveSubscription(countryCode);
      if (subscriptionDataResponse) {
        setSubscriptionData({
          ...subscriptionData,
          ...subscriptionDataResponse
        });
      }
      const maxPricePack = subscriptionDataResponse?.maxPricePack;
      let hasActiveSubscription = false;
      if (
        subscriptionDataResponse &&
        subscriptionDataResponse.currentActiveSubscriptions &&
        subscriptionDataResponse.currentActiveSubscriptions.length > 0
      ) {
        hasActiveSubscription = true;
      }
      setUserTypeInLS(
        hasActiveSubscription ? UserTypes.PREMIUM : UserTypes.REGISTER
      );

      if (maxPricePack) {
        const packType = fetchEntitlementUserType(maxPricePack);
        if (packType) {
          setUserPackType(packType);
        }
      }
    }
  };

  const getTvodRentals = async () => {
    const { fetchTvodPlans } = await import(
      'src/services/apiRequests/RentalService'
    );
    const tvodRentalsResponse = await fetchTvodPlans();
    if (tvodRentalsResponse) {
      setTvodRentals({ ...tvodRentals, tvodPlans: tvodRentalsResponse });
    }
  };

  const HamburgerMenuButton = () => {
    return (
      <span>
        <button
          aria-label="hamburger menu icon"
          css={style.hamburgerMenuButton}
          onClick={hamburgerMenuCLickHandler}
          data-testid="hamburger-menu-btn">
          <div css={style.hamburgerMenuIcon}></div>
        </button>
      </span>
    );
  };

  const hamburgerMenuCLickHandler = () => {
    if (!showHamburgerMenu) {
      burgerCTAMixpanelEvent(MIXPANEL_HAMBURGER, MIXPANEL_HEADER);
    } else {
      burgerCTAMixpanelEvent(MIXPANEL_CROSS, MIXPANEL_CTA);
    }
    setShowHamburgerMenu((prevState) => !prevState);
  };

  const handleProfileMenuClick = () => {
    setShowProfileMenu((prevState) => !prevState);
  };

  const scrollHandler = () => {
    const scrollTopVal = window.pageYOffset;
    if (
      scrollTopVal > scrollHeight &&
      scrollHeight > 0 &&
      !headerScrollPosition
    ) {
      setHeaderScrollPosition(true);
    } else if (scrollTopVal < scrollHeight && headerScrollPosition) {
      setHeaderScrollPosition(false);
    }
  };
  const navigationPageHandler = (e: any) => {
    const userType = getLocalStorage(USER_TYPE);
    const parterName = getLocalStorage(PARTNER_NAME);
    if (
      isSubscriptionPage &&
      referralSubscriptionNudgeEnabled &&
      userType === UserTypes.REGISTER &&
      !parterName
    ) {
      setPopups({
        showReferralSubscriptionNudge: true,
        referralRedirectLink: NavMenuLinks.HOME,
        referralSourceElement: HeaderComponentImageAlt.ZEE5_LOGO
      });
    } else {
      deleteSessionStorage(VISITED_PAYMENT_PAGE);
      navigationHandler(
        NavMenuLinks.HOME,
        HeaderComponentImageAlt.ZEE5_LOGO,
        e
      );
    }
  };

  const isNewSubscription = isFeatureEnabled(
    remoteConfig,
    FeatureFlagConstants.FEATURE_NEW_SUBSCRIPTION_UI
  );

  if (isSubscriptionPage && isNewSubscription) {
    return innerWidth < DEVICE_WIDTH_CONSTANTS.deviceWidth1200 ? null : (
      <style.NewSubsHeader>
        <Image
          src={HeaderImageUrl.ZEE5_BACK_BUTTON}
          alt={HeaderComponentImageAlt.BACK}
          width={48}
          height={48}
          onClick={navigationPageHandler}
        />
        <div
          css={css`
            width: ${logoWidth};
          `}
          data-testid="zee5-logo">
          <Image
            src={HeaderImageUrl.ZEE5_LOGO_URL}
            alt={HeaderComponentImageAlt.ZEE5_LOGO}
            width={logoWidth}
            height={logoWidth}
            onClick={navigationPageHandler}
          />
        </div>
      </style.NewSubsHeader>
    );
  }

  return headerStrings ? (
    <header
      onClickCapture={clearSubscriptionNudgePopupTimer}
      css={[
        style.headerWrapper,
        style.headerBgStyle(headerScrollPosition, isSubscriptionPage)
      ]}>
      <div css={style.headerContainer} data-testid="headerContainer">
        <div css={style.headerLeftContainer}>
          {isMobile && isBackButtonRequired && <BackButton />}
          <div css={{ width: logoWidth }} data-testid="zee5-logo">
            <Image
              src={HeaderImageUrl.ZEE5_LOGO_URL}
              alt={HeaderComponentImageAlt.ZEE5_LOGO}
              width={logoWidth}
              height={logoWidth}
              style={{ cursor: 'pointer' }}
              onClick={navigationPageHandler}
            />
          </div>
          {!isSubscriptionPage &&
            innerWidth >= DEVICE_WIDTH_CONSTANTS.deviceWidth1200 && (
              <div css={style.navMenuContainer}>
                {!hideMenu && (
                  <NavMenu isLapser={isLapser} defaultMenuCount={menuCount} />
                )}
              </div>
            )}
        </div>
        <div css={style.headerRightContainer(hideMenu)}>
          {!isSubscriptionPage && (
            <>
              <SearchBox setHideMenu={setHideMenu} hideMenu={hideMenu} />
              <LanguagePopup />
            </>
          )}
          {innerWidth >= DEVICE_WIDTH_CONSTANTS.deviceWidth1200 && (
            <>
              {isUserLoggedIn &&
              !isSubscriptionPage &&
              innerWidth >= DEVICE_WIDTH_CONSTANTS.deviceWidth1200 ? (
                <button
                  css={style.userProfileBtn}
                  data-tooltip="My profile"
                  onClick={handleProfileMenuClick}></button>
              ) : null}
              {showProfileMenu && (
                <Portal onClick={handleProfileMenuClick} show={showProfileMenu}>
                  <ProfileMenu
                    show={showProfileMenu}
                    handleProfileMenuClick={handleProfileMenuClick}
                  />
                </Portal>
              )}
              {isUserLoggedIn && isSubscriptionPage ? (
                <div css={style.userInfoContainer}>
                  <Image
                    src={HeaderImageUrl.USER_LOGO_URL}
                    alt={HeaderComponentImageAlt.USER_LOGO}
                    width={25}
                    height={25}
                  />
                  <div css={style.userInfo}>
                    <div>
                      {headerStrings.WELCOME + ' ' + getUserName(usersData)}
                    </div>
                    <div>{usersData && getUserContactInfo(usersData)}</div>
                  </div>
                </div>
              ) : null}
              {!isUserLoggedIn ? (
                <a
                  href={HeaderLinks.SIGN_IN}
                  css={style.outlineButton}
                  onClick={(e) => {
                    navigationHandler(HeaderLinks.SIGN_IN, MIXPANEL_LOGIN, e);
                  }}
                  data-testid="login-btn">
                  {headerStrings.LOGIN}
                </a>
              ) : null}
            </>
          )}
          {!isSubscriptionPage && shouldShowGlobalRegister ? (
            <a
              href={HeaderLinks.REGISTER}
              css={style.outlineButton}
              onClick={(e) => {
                navigationHandler(HeaderLinks.REGISTER, MIXPANEL_REGISTER, e);
              }}
              data-testid="register-btn">
              {REGISTER}
            </a>
          ) : null}
          {!isSubscriptionPage &&
          ((showSubsBtn && !isGetStarted && !isQwikcilverSubscribed) ||
            isBotRequest) ? (
            isLapser && lapserDetails ? (
              <AnimatedButton
                lapserDetails={lapserDetails.lapserAdvanceRenewalNudge}
              />
            ) : (
              <div css={style.headerMobileStyle} data-testid="subscribe-btn">
                <a
                  href={HamburgerMenuLinks.BUY_PLAN}
                  css={style.buyPlanOrUpgradeButton}
                  onClick={(e) => {
                    navigationHandler(
                      HamburgerMenuLinks.BUY_PLAN,
                      MIXPANEL_BUY_PLAN,
                      e
                    );
                  }}>
                  {!IS_RENEWAL_PLAN_SAME_AS_EXPIRED_FOR_LAPSER ? (
                    <span css={style.crownIcon} />
                  ) : null}
                  {subscribeBtnTxt}
                </a>
              </div>
            )
          ) : null}
          {(isGetStarted && !isSubscriptionPage && !isExpiredPlans) ||
          (showSubsBtn && isGetStarted && !isSubscriptionPage) ? (
            (isUserLoggedIn && isSubscribedUser && isUpgrade) ||
            (isUserLoggedIn && !isSubscribedUser) ? (
              <div css={style.headerMobileStyle} data-testid="subscribe-btn">
                <a
                  href={HamburgerMenuLinks.BUY_PLAN}
                  css={style.buyPlanOrUpgradeButton}
                  onClick={(e) => {
                    navigationHandler(
                      HamburgerMenuLinks.BUY_PLAN,
                      MIXPANEL_BUY_PLAN,
                      e
                    );
                  }}>
                  <span css={style.crownIcon} />
                  {subscribeBtnTxt}
                </a>
              </div>
            ) : (
              !isUserLoggedIn &&
              !isSubscriptionPage && (
                <div
                  css={style.headerMobileStyle}
                  data-testid="get-started-btn">
                  <a
                    href={HeaderLinks.REGISTER}
                    css={style.outlineButton}
                    onClick={(e) => {
                      navigationHandler(
                        HeaderLinks.REGISTER,
                        MIXPANEL_BUY_PLAN,
                        e
                      );
                    }}>
                    {GET_STARTED}
                  </a>
                </div>
              )
            )
          ) : null}

          {!isSubscriptionPage && <HamburgerMenuButton />}
          {showHamburgerMenu && (
            <HamburgerMenuWrapper
              hamburgerMenuCLickHandler={hamburgerMenuCLickHandler}
              showHamburgerMenu={showHamburgerMenu}
              isMobile={isMobile}
            />
          )}
        </div>
      </div>

      <div css={style.mobileNavBarContainer}>
        {!isSubscriptionPage &&
          !isBackButtonRequired &&
          innerWidth < DEVICE_WIDTH_CONSTANTS.deviceWidth900 && (
            <NavMenu isLapser={isLapser} defaultMenuCount={menuCount} />
          )}
      </div>
    </header>
  ) : null;
};
export default Header;
